import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRange } from '../../context/RangeContext';
import RangeMatrix from '../../components/RangeMatrix/RangeMatrix';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';
import { formatTitle } from '../../utils/rangeFormatters';

import './view-range-screen.css';
import '../../components/RangeMatrix/range-matrix.css'

export const ViewRangeScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeRange, loadRange } = useRange();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const rangeLoadedRef = useRef(false);
  
  const state = location.state || {};
  const config = state.config || {};
  const returnTo = state.returnTo || '/answer';

  useEffect(() => {
    // Skip loading if we've already done it
    if (rangeLoadedRef.current) {
      setIsLoading(false);
      return;
    }

    const loadRangeData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Simple validation for required fields
        const position = config.heroPosition || state.heroPosition || 'btn';
        const betType = config.betType || state.betType || 'open';
        
        if (!position) {
          throw new Error('Position is required');
        }
        
        // Create a path for the range file based on position and bet type
        const positionLower = position.toLowerCase();
        let rangePath;
        
        // Use range path from config if available
        if (config.range) {
          rangePath = config.range;
        } else if (betType === 'FACING_3BET_4BET') {
          const threeBetPos = config.threeBetPosition?.toLowerCase() || 'sb';
          rangePath = `/database/preflop/ranges/${positionLower}/${positionLower}_4bet-${threeBetPos}_3bet.csv`;
        } else if (betType === 'FACING_OPEN_3BET') {
          const openPos = config.openPosition?.toLowerCase() || 'hj';
          rangePath = `/database/preflop/ranges/${positionLower}/${positionLower}_3bet-${openPos}_open.csv`;
        } else {
          rangePath = `/database/preflop/ranges/${positionLower}/${positionLower}_open.csv`;
        }
        
        console.log('ViewRangeScreen: Loading range once with path:', rangePath);
        
        // Attempt to load the range
        await loadRange({
          position: positionLower,
          betType: betType,
          rangePath: rangePath
        });
        
        rangeLoadedRef.current = true;
        setError(null);
      } catch (error) {
        console.error('Error loading range:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadRangeData();
  }, []); // Empty dependency array - only run once

  const handleBackToAnswer = () => {
    // Don't pass any range data back to prevent state issues
    navigate(returnTo, { 
      state: state // Preserve the original state when going back
    });
  };
  
  const handleContinue = () => {
    // Instead of navigating with state, use GameContext approach
    console.log('ViewRangeScreen: Continue button clicked - using direct navigation');
    
    // Navigate to preflop with minimal state to avoid creating a loop
    navigate('/preflop', { 
      state: null // Reset state completely to avoid loops
    });
  };

  if (error) {
    return (
      <div className="range-view-config">
        <div className="range-view-content">
          <div className="range-view-navigation">
            <div className="type-subtitle">6 Max Cash Game</div>
          </div>
          <div className="error-message">
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Generate a readable title based on position and bet type
  const position = config.heroPosition || state.heroPosition || 'BTN';
  const betType = config.betType || state.betType || 'OPEN';

  return (
    <div className="range-view-config">
      <div className="range-view-content">
        <div className="range-view-navigation">
          <div className="type-subtitle">6 Max Cash Game</div>
        </div>

        <div className="title-container">
          <h1 className="view-range-title">
            {formatTitle(
              position,
              betType,
              {
                openPosition: config?.openPosition,
                threeBetPosition: config?.threeBetPosition,
                mappedBetType: config?.mappedBetType,
                callType: config?.callType
              }
            )}
          </h1>
        </div>

        {isLoading ? (
          <div className="loading">Loading range...</div>
        ) : (
          <>
            <div>
              <RangeMatrix 
                activeRange={activeRange}
                variant="view-range"
                size='large'
              />
            </div>

            <div className="view-range-button-container">
              <ButtonLarge
                text="Back to Answer"
                buttonLargeClassName="button-large view-range-button answer-button"
                onClick={handleBackToAnswer}
              />
              <ButtonLarge
                text="Continue"
                buttonLargeClassName="button-large continue-button answer-button"
                onClick={handleContinue}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};